import { useState, useEffect } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/azhome/AzHeader";
import { useAuth } from "../context/AuthContext";
import SocialAuth from "../components/SocialAuth";
import { SliderCaptcha } from "../components/SliderCaptcha";
import "../auth.css";

const Login = () => {
  const { login, verifyLoginOtp, resendVerification, user, loading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const [capToken, setCapToken] = useState("");
  const [capKey, setCapKey] = useState(0);
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  if (!loading && user) {
    return <Navigate to="/dashboard" replace />;
  }

  const goHome = () => navigate(process.env.REACT_APP_ADMIN_MODE === "true" ? "/admin" : "/dashboard");

  const onResend = async () => {
    try { await resendVerification(email); } catch {} finally { setResent(true); }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setUnverified(false); setResent(false); setBusy(true);
    try {
      const res = await login(email, password, needs2fa ? code : undefined, needCaptcha ? capToken : undefined);
      setNeedCaptcha(false); setCapToken("");
      if (res.requires_2fa) { setNeeds2fa(true); setBusy(false); return; }
      if (res.requires_otp) { setOtpStep(true); setCooldown(60); setBusy(false); return; }
      goHome();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (needCaptcha || err.response?.status === 428) { setCapToken(""); setCapKey((k) => k + 1); }
      if (err.response?.status === 428) setNeedCaptcha(true);
      if (detail && typeof detail === "object" && detail.code === "email_unverified") {
        setUnverified(true);
        setError(detail.message || "Please verify your email before logging in.");
      } else {
        setError((typeof detail === "string" && detail) || "Login failed. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await verifyLoginOtp(email, otp.trim());
      goHome();
    } catch (err) {
      setError((typeof err.response?.data?.detail === "string" && err.response.data.detail) || t("auth.otpInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const onResendOtp = async () => {
    if (cooldown > 0) return;
    setError("");
    try {
      await login(email, password, needs2fa ? code : undefined);
      setCooldown(60);
    } catch (err) {
      setError((typeof err.response?.data?.detail === "string" && err.response.data.detail) || t("auth.otpResendFailed"));
    }
  };

  return (
    <>
      <Header />
      <main className="auth-page">
        <div className="auth-card">
          {otpStep ? (
            <>
              <p className="overline">{t("auth.otpOverline")}</p>
              <h1>{t("auth.otpTitle")}</h1>
              <p className="auth-sub">{t("auth.otpSub", { email })}</p>
              {error && <div className="auth-error" data-testid="login-otp-error">{error}</div>}
              <form onSubmit={onVerifyOtp}>
                <div className="auth-field">
                  <label htmlFor="otp">{t("auth.otpCodeLabel")}</label>
                  <input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    placeholder={t("auth.otpPh")}
                    data-testid="login-otp-input"
                  />
                </div>
                <button className="button blue auth-submit" type="submit" disabled={busy || otp.length !== 6} data-testid="login-otp-submit">
                  {busy ? t("auth.verifying") : t("auth.otpVerifyBtn")}
                </button>
              </form>
              <p className="auth-switch">
                <button type="button" onClick={onResendOtp} disabled={cooldown > 0} data-testid="login-otp-resend"
                  style={{ background: "none", border: "none", color: cooldown > 0 ? "var(--muted,#94a3b8)" : "var(--accent,#0B5CFF)", cursor: cooldown > 0 ? "default" : "pointer", fontWeight: 600 }}>
                  {cooldown > 0 ? t("auth.otpResendIn", { s: cooldown }) : t("auth.otpResend")}
                </button>
              </p>
            </>
          ) : (
            <>
              <p className="overline">{t("auth.welcomeBack")}</p>
              <h1>{t("auth.loginTitle")}</h1>
              <p className="auth-sub">{t("auth.loginSub")}</p>
              {error && <div className="auth-error" data-testid="login-error">{error}</div>}
              {unverified && (
                <button type="button" onClick={onResend} data-testid="login-resend-verification"
                  style={{ width: "100%", marginBottom: 14, background: "none", border: "1px solid var(--line,#e2e8f0)", borderRadius: 10, padding: "11px", cursor: "pointer", fontWeight: 600 }}>
                  {resent ? t("auth.verifyResent") : t("auth.resendVerification")}
                </button>
              )}
              <form onSubmit={onSubmit}>
                <div className="auth-field">
                  <label htmlFor="email">{t("auth.emailLabel")}</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={needs2fa}
                    data-testid="login-email"
                  />
                </div>
                <div className="auth-field">
                  <label htmlFor="password">{t("auth.passwordLabel")}</label>
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    disabled={needs2fa}
                    data-testid="login-password"
                  />
                </div>
                {needs2fa && (
                  <div className="auth-field">
                    <label htmlFor="totp">{t("auth.authCodeLabel")}</label>
                    <input
                      id="totp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      autoFocus
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                      placeholder={t("auth.codePh")}
                      data-testid="login-totp"
                    />
                    <p className="auth-sub" style={{ marginTop: 6, fontSize: 12 }}>{t("auth.authCodeHint")}</p>
                  </div>
                )}
                {needCaptcha && <SliderCaptcha key={capKey} onVerified={setCapToken} />}
                <button className="button blue auth-submit" type="submit" disabled={busy || (needs2fa && code.length !== 6) || (needCaptcha && !capToken)} data-testid="login-submit">
                  {busy ? t("auth.loggingIn") : needs2fa ? t("auth.verifyLogin") : t("auth.loginBtn")}
                </button>
              </form>
              <SocialAuth label={t("auth.orContinueWith") || "or continue with"} />
              <p className="auth-google-hint" data-testid="login-google-hint" style={{ marginTop: 12, fontSize: 12.5, color: "var(--muted,#94a3b8)", textAlign: "center", lineHeight: 1.5 }}>
                {t("auth.googlePasswordHint")}
              </p>
              <p className="auth-switch" style={{ marginTop: 14 }}>
                <Link to="/forgot-password" data-testid="login-forgot-password">{t("auth.forgotPasswordLink")}</Link>
              </p>
              <p className="auth-switch">
                {t("auth.newToImperio")} <Link to="/register">{t("auth.createAccount")}</Link>
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default Login;
